/**
 * Admin ListingsPage Component
 *
 * Admin page for managing platform listings.
 */

import React, { useState } from 'react';
import { AdminLayout } from '../layout';
import {
  useAdminListings,
  useListingActions,
  AdminListingsFilter,
  AdminListing,
} from './hooks';
import { ListingFilters, ListingRow } from './components';

export const ListingsPage: React.FC = () => {
  const [filter, setFilter] = useState<AdminListingsFilter>({});
  const [page, setPage] = useState(0);
  const [selectedListings, setSelectedListings] = useState<string[]>([]);

  const { listings, stats, totalCount, isLoading, hasMore, totalPages } =
    useAdminListings(filter, page);
  const {
    pauseListing,
    unpauseListing,
    featureListing,
    approveListing,
    deleteListing,
    bulkPause,
    bulkFeature,
  } = useListingActions();

  const handleSelectAll = (checked: boolean) => {
    setSelectedListings(checked ? listings.map((l) => l.id) : []);
  };

  const handleSelectListing = (listingId: string, selected: boolean) => {
    setSelectedListings((prev) =>
      selected ? [...prev, listingId] : prev.filter((id) => id !== listingId)
    );
  };

  const handleListingAction = (
    listing: AdminListing,
    action: 'view' | 'pause' | 'unpause' | 'feature' | 'unfeature' | 'approve' | 'delete'
  ) => {
    switch (action) {
      case 'pause':
        pauseListing(listing.id);
        break;
      case 'unpause':
        unpauseListing(listing.id);
        break;
      case 'feature':
        featureListing(listing.id, true);
        break;
      case 'unfeature':
        featureListing(listing.id, false);
        break;
      case 'approve':
        approveListing(listing.id);
        break;
      case 'delete':
        if (confirm('Are you sure you want to delete this listing?')) {
          deleteListing(listing.id);
        }
        break;
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Listings</h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage and moderate platform listings
            </p>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div
            onClick={() => setFilter({ ...filter, status: 'active' })}
            className="bg-white rounded-lg p-4 border border-gray-200 cursor-pointer hover:border-primary-300 transition-colors"
          >
            <p className="text-sm text-gray-500">Active</p>
            <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
          </div>
          <div
            onClick={() => setFilter({ ...filter, status: 'pending' })}
            className="bg-white rounded-lg p-4 border border-yellow-200 cursor-pointer hover:border-yellow-400 transition-colors"
          >
            <p className="text-sm text-gray-500">Pending Review</p>
            <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
          </div>
          <div
            onClick={() => setFilter({ ...filter, status: 'paused' })}
            className="bg-white rounded-lg p-4 border border-gray-200 cursor-pointer hover:border-primary-300 transition-colors"
          >
            <p className="text-sm text-gray-500">Paused</p>
            <p className="text-2xl font-bold text-gray-900">{stats.paused}</p>
          </div>
          <div
            onClick={() => setFilter({ ...filter, isFeatured: true })}
            className="bg-white rounded-lg p-4 border border-gray-200 cursor-pointer hover:border-primary-300 transition-colors"
          >
            <p className="text-sm text-gray-500">Featured</p>
            <p className="text-2xl font-bold text-gray-900">{stats.featured}</p>
          </div>
        </div>

        {/* Filters */}
        <ListingFilters filter={filter} onChange={setFilter} totalCount={totalCount} />

        {/* Listings Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto" />
              <p className="text-gray-500 mt-2">Loading listings...</p>
            </div>
          ) : listings.length === 0 ? (
            <div className="p-8 text-center">
              <BuildingIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No listings found</p>
              {(filter.search ||
                filter.status ||
                filter.propertyType ||
                filter.location ||
                filter.isFeatured !== undefined) && (
                <button
                  onClick={() => setFilter({})}
                  className="mt-2 text-primary-500 hover:text-primary-600"
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="p-4 text-left w-10">
                        <input
                          type="checkbox"
                          checked={
                            selectedListings.length > 0 &&
                            selectedListings.length === listings.length
                          }
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                        />
                      </th>
                      <th className="p-4 text-left text-sm font-medium text-gray-600">
                        Listing
                      </th>
                      <th className="p-4 text-left text-sm font-medium text-gray-600">
                        Host
                      </th>
                      <th className="p-4 text-left text-sm font-medium text-gray-600">
                        Type
                      </th>
                      <th className="p-4 text-left text-sm font-medium text-gray-600">
                        Status
                      </th>
                      <th className="p-4 text-left text-sm font-medium text-gray-600">
                        Price
                      </th>
                      <th className="p-4 text-left text-sm font-medium text-gray-600">
                        Stats
                      </th>
                      <th className="p-4 text-left text-sm font-medium text-gray-600">
                        Created
                      </th>
                      <th className="p-4 text-left text-sm font-medium text-gray-600 w-12">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {listings.map((listing) => (
                      <ListingRow
                        key={listing.id}
                        listing={listing}
                        isSelected={selectedListings.includes(listing.id)}
                        onSelect={(selected) =>
                          handleSelectListing(listing.id, selected)
                        }
                        onAction={(action) => handleListingAction(listing, action)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="p-4 border-t border-gray-200 flex items-center justify-between">
                <span className="text-sm text-gray-500">
                  Page {page + 1} of {totalPages}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={!hasMore}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Bulk Actions Bar */}
        {selectedListings.length > 0 && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white rounded-xl shadow-2xl px-6 py-4 flex items-center gap-6 z-40">
            <span className="text-sm">
              <span className="font-medium">{selectedListings.length}</span> listing
              {selectedListings.length !== 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center gap-3">
              <button
                onClick={() => bulkPause(selectedListings)}
                className="px-3 py-1.5 bg-white text-gray-900 text-sm rounded-lg hover:bg-gray-100 transition-colors"
              >
                Pause
              </button>
              <button
                onClick={() => bulkFeature(selectedListings, true)}
                className="px-3 py-1.5 bg-yellow-500 text-white text-sm rounded-lg hover:bg-yellow-600 transition-colors"
              >
                Feature
              </button>
              <button
                onClick={() => {
                  if (confirm(`Delete ${selectedListings.length} listings?`)) {
                    selectedListings.forEach((id) => deleteListing(id));
                    setSelectedListings([]);
                  }
                }}
                className="px-3 py-1.5 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
            <button
              onClick={() => setSelectedListings([])}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <XIcon className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

// Icons
const BuildingIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
    />
  </svg>
);

const XIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

export default ListingsPage;
